import { apiCall } from '@/lib/api';
import {
  createInfiniteQuery,
  createMutation,
  createQuery,
  useQueryClient,
} from '@tanstack/solid-query';
import type {
  AssignDeviceBody,
  CreateDeviceBody,
  DeviceOs,
  DeviceStatus,
  UpdateDeviceBody,
} from '@uchat-team-management/shared';
import {
  assignDevice,
  createDevice,
  deleteDevice,
  getDevice,
  getMyDevices,
  listDevices,
  searchDevicesByImei,
  updateDevice,
} from './api';

export interface DevicesFilters {
  q?: string;
  status?: DeviceStatus[];
  os?: DeviceOs;
  assignedUserId?: string | 'none';
}

export const devicesKey = (filters: DevicesFilters) => ['devices', filters] as const;
export const deviceKey = (id: string) => ['devices', id] as const;

export const useDevices = (filters: () => DevicesFilters) =>
  createInfiniteQuery(() => ({
    queryKey: devicesKey(filters()),
    queryFn: ({ pageParam }) =>
      apiCall(listDevices, { query: { ...filters(), cursor: pageParam || undefined } }),
    initialPageParam: '',
    getNextPageParam: (last) => last.meta.nextCursor ?? undefined,
  }));

export const useMyDevices = () =>
  createQuery(() => ({
    queryKey: ['devices', 'mine'] as const,
    queryFn: () => apiCall(getMyDevices, {}),
  }));

export const useDevice = (deviceId: () => string) =>
  createQuery(() => ({
    queryKey: deviceKey(deviceId()),
    queryFn: () => apiCall(getDevice, { params: { deviceId: deviceId() } }),
  }));

export const useCreateDevice = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (body: CreateDeviceBody) => apiCall(createDevice, { body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['devices'] }),
  }));
};

export const useUpdateDevice = (deviceId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (body: UpdateDeviceBody) => apiCall(updateDevice, { params: { deviceId }, body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['devices'] });
      qc.invalidateQueries({ queryKey: ['devices', deviceId] });
    },
  }));
};

export const useDeleteDevice = (deviceId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: () => apiCall(deleteDevice, { params: { deviceId } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['devices'] }),
  }));
};

export const useImeiSearch = (imei: () => string) =>
  createQuery(() => ({
    enabled: imei().trim().length >= 2,
    queryKey: ['devices', 'search', imei()] as const,
    queryFn: () => apiCall(searchDevicesByImei, { query: { imei: imei().trim() } }),
    staleTime: 5_000,
  }));

export const useAssignDevice = (deviceId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (body: AssignDeviceBody) => apiCall(assignDevice, { params: { deviceId }, body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['devices'] });
      qc.invalidateQueries({ queryKey: ['devices', deviceId] });
    },
  }));
};
